import { ITEM_ID_MAX, ITEM_ID_PATTERN } from './pages.js';
import { LINKS_MAX, MAX_LINK_LABEL_LENGTH } from './values.js';
/** A content zone. Both consumers cap it here and nowhere else. */
export const RICHTEXT_MAX = 20_000;
/** The caps inside a list entry, which no `SlotSpec.max` can express. */
export const ROW_TEXT_MAX = 400;
export const IMAGE_SRC_MAX = 2_000;
export const IMAGE_ALT_MAX = 300;
export const IMAGE_CAPTION_MAX = 400;
export const QA_QUESTION_MAX = 400;
export const QA_ANSWER_MAX = 4_000;
/** A short line of plain text, and a paragraph of it. */
export const TEXT_MAX = 400;
export const PROSE_MAX = 4_000;
/** The `features` block's per-entry caps. */
export const FEATURE_TITLE_MAX = 80;
export const FEATURE_TEXT_MAX = 300;
/**
 * The `timeline` view's timestamp — "06:40", "2:07 AM", "Day 1".
 *
 * Twenty characters, because it is a *stamp* and not a sentence: the view sets
 * it as the moment beside the title, and anything that wraps stops being a
 * timestamp and starts being a second headline. Optional on every view; the
 * other four draw nothing for it except `numbered`, which prefers it to the
 * counter when it is there (see the `variant` slot's hint).
 */
export const FEATURE_LABEL_MAX = 20;
/** The `showcase` block's per-slice caps. */
export const PANEL_TITLE_MAX = 80;
export const PANEL_TEXT_MAX = 240;
export const PANEL_BULLET_MAX = 120;
export const PANEL_BULLETS_MAX = 4;
/**
 * The `pricing` block's per-tier caps, and how many tiers a band may carry.
 *
 * **Four**, not "as many as you sell". Every pricing band in the references is
 * three columns with one of them marked — TimeKey's three
 * (`docs/design/references/timekey.md` §1 band 11), and the product sites in
 * `docs/design/references/notes.md` (Cal.com, Calendly) run three with a
 * fourth "Enterprise" column that is a *Contact us* rather than a number. Four
 * is that fourth column; a fifth is a comparison table, which is a page and not
 * a band.
 *
 * `TIER_PRICE_MAX` is deliberately the smallest of them: twenty-four
 * characters is "$79", "From $3 000" or "Custom", and a price field long enough
 * to hold a sentence is a price field somebody will put terms in — the terms
 * belong in `note`, and the terms that are true of every tier belong in the
 * block's `fine_print`.
 */
export const TIER_NAME_MAX = 40;
export const TIER_PRICE_MAX = 24;
export const TIER_PERIOD_MAX = 40;
export const TIER_NOTE_MAX = 160;
export const TIER_BULLETS_MAX = 6;
export const TIER_BULLET_MAX = 120;
export const TIERS_MAX = 4;
/**
 * **The outbound-links slot, declared once and spread into the blocks that may
 * carry one.**
 *
 * One declaration rather than three copies, for `BAND_SLOTS`' reason: the cap,
 * the label and the hint are the *same* affordance wherever it appears, and
 * three copies is three places for one of them to drift by a number.
 *
 * Three blocks have it and the rest deliberately do not. `shop` — the reason it
 * exists ("Shop the full line through our partner link"). `showcase` — a
 * photographic slice about a brand is the other place an owner points outward.
 * `footer` — where every site in the survey keeps the links that are not
 * navigation. A hero does not get one: a hero has exactly one call to action
 * and a second, outbound one beside it is a page sending its visitor away
 * before it has said what it is.
 */
export const LINKS_SLOT = {
    name: 'links',
    kind: 'links',
    label: 'Outbound links',
    max: LINKS_MAX,
    entry_max: MAX_LINK_LABEL_LENGTH,
    hint: 'Up to six { label, url } rows that leave the site — an affiliate or partner address, ' +
        'the brand you stock. https: only; they open in a new tab with rel="noopener noreferrer", ' +
        'and nothing is added to the URL you give.',
};
/**
 * The icons a `features` entry may name — a **closed set drawn by the bundle**,
 * never a URL and never tenant bytes: an icon is chrome, and chrome is ours
 * (the same line that keeps refusal sentences server-authored). Adding a name
 * here plus its path in the bundle is the whole cost of a new one.
 */
export const FEATURE_ICONS = [
    'fire',
    'sauna',
    'skylight',
    'creek',
    'pine',
    'peak',
    'ski',
    'bike',
    'snowshoe',
    'bed',
    'mug',
    'map',
    'key',
    'sun',
    'moon',
    'star',
];
/* ------------------------------------------------------------------ */
/* The band system                                                     */
/* ------------------------------------------------------------------ */
/**
 * Every band-shaped block carries these two slots, spread from one constant so
 * an eleventh copy cannot drift from the other ten.
 *
 * **`tone`** is what killed "the page reads like a Word doc" (the AlpineCreek
 * finding, one wave after Pink Perry's owner said the same of a hero): a page
 * whose every section sits on the same endless paper has no rhythm, and the
 * token vocabulary could name colors but no section could *choose* one. The
 * four tones are projections of tokens the tenant already owns — `paper` and
 * `alt` are the two grounds, `accent` is the brand color as a band (its text
 * flips to `accent-ink`), `night` is `ink` as a ground (text flips to paper) —
 * so a tone can never disagree with the palette, and a tenant who configures
 * nothing gets the page they always had.
 *
 * **`divider`** is the seam between bands drawn as terrain — a mountain
 * ridge or a creek curve, inline SVG, painted in the *previous* band's ground
 * so the edge reads as the land changing rather than as a border. The bundle
 * computes the neighbor; config only chooses the shape.
 */
export const BAND_SLOTS = [
    {
        name: 'tone',
        kind: 'enum',
        label: 'Band tone',
        max: 0,
        options: ['paper', 'alt', 'accent', 'night'],
        fallback: 'paper',
        hint: 'The ground this section sits on. paper and alt are the two light grounds; accent and night flip the text to stay readable.',
    },
    {
        name: 'divider',
        kind: 'enum',
        label: 'Divider',
        max: 0,
        options: ['none', 'ridge', 'creek'],
        fallback: 'none',
        hint: 'A terrain seam drawn above this section, painted in the previous band’s ground.',
    },
    {
        // The eyebrow: "01 · The property" — the tracked line editorial pages put
        // over a section title. Rendered by the band wrapper, above the block.
        name: 'kicker',
        kind: 'text',
        label: 'Eyebrow',
        max: TEXT_MAX,
        hint: 'A short tracked line above the section — a place, a number, a promise. Empty renders nothing.',
    },
    {
        /**
         * The immersive ground — "bring the outside in" (the AlpineCreek brief):
         * a photograph as the band's full surface, under a dark scrim the bundle
         * owns, text flipped light. Materials as page structure — bark, embers,
         * fog on the river — not only as pictures in frames. When set it
         * overrides `tone` visually; the tone still names the divider color a
         * FOLLOWING band paints, so the seam stays honest.
         *
         * Named `backdrop`, not `image`: the story block already owns an `image`
         * slot, and the first render proved what a shared name does — the Great
         * Lodge photograph was hijacked as its own band's ground and the words
         * drowned in it. Band slots ride every block, so their names must collide
         * with none.
         */
        name: 'backdrop',
        kind: 'image_url',
        label: 'Band photograph',
        max: IMAGE_SRC_MAX,
        hint: 'A full-bleed photographic ground for this band, under a dark scrim; the band’s text goes light. https: or an inline data:image URI.',
    },
    {
        /**
         * Which way the scrim over a `backdrop` goes. `dusk` is the cinematic
         * ground the band system shipped with — a dark gradient, light type,
         * night-glass cards — and it is right for a photograph of a lodge at
         * sundown. It was wrong for Love Spa's hibiscus-and-shells sheet, which
         * is wallpaper: the tenant's stylesheet had to repaint the scrim in cream
         * and put the type back to ink, six rules for one idea. `light` is that
         * idea as a choice: a paper-coloured wash, ink type, milk-glass cards.
         * Ignored without a backdrop.
         */
        name: 'wash',
        kind: 'enum',
        label: 'Backdrop wash',
        max: 0,
        options: ['dusk', 'light'],
        fallback: 'dusk',
        hint: 'How the band photograph is washed. dusk darkens it and flips the type light; light veils it in the page colour and keeps the type ink. Ignored without a band photograph.',
    },
    {
        /**
         * **The styles axis** (`docs/site-section-system.md` §9.8) — the owner's
         * third question ("a bunch of styles that can be applied to each of the
         * layouts") answered once. Every other meaning of the word was already a
         * preset, a token or a variant; the one that was nowhere is *how a
         * block's cards, fields and chips are drawn*. Love Spa's glass menu was
         * baked into the `services.cards` view, and putting the same glass on its
         * features, testimonials and team is exactly the rung-4 CSS §1 measured.
         *
         * It is a **band slot, not a token**, because one page mixes them: Love
         * Spa draws glass on the sheet band and flat cards on paper. A site-wide
         * setting would be the wrong shape for the only page that asked for it.
         *
         * `auto` is each view's own default — the services cards stay glass, the
         * plain grid stays a card, a features row stays a rule — so every
         * published document renders **byte-for-byte**. The other four are one
         * rule set in the stylesheet, reached from a `data-surface` attribute the
         * band wrapper carries, and every card, field and chip in the bundle
         * reads it. A fifth surface is one CSS block, never a per-block change.
         */
        name: 'surface',
        kind: 'enum',
        label: 'Surface',
        max: 0,
        options: ['auto', 'flat', 'card', 'glass', 'outlined'],
        fallback: 'auto',
        hint: 'How this band draws its cards, fields and chips. auto leaves each block its own default; flat removes the ground entirely; card lifts them onto the surface colour; glass frosts them over the band; outlined keeps one hairline and nothing else.',
    },
];
/**
 * The view axis (`docs/site-section-system.md` §3): one dataset, many views.
 * A section's data is its slots and the catalog behind it; its *variant* is a
 * named rendering the bundle ships, designed and responsive, chosen in the
 * console. The first option is always today's rendering, so a row that names
 * none keeps rendering byte-for-byte. A block gains the slot with its second
 * view and never before — a select with one choice is a control that lies.
 */
function variantSlot(options, hint, alias) {
    const fallback = options[0];
    if (fallback === undefined)
        throw new Error('a variant slot needs at least one view');
    return {
        name: 'variant',
        kind: 'enum',
        label: 'Variant',
        max: 0,
        options,
        fallback,
        hint,
        ...(alias === undefined ? {} : { alias }),
    };
}
/**
 * How many across, where a block lays its items out as a grid. `auto` is the
 * auto-fit every existing tenant has; a number pins it (six features as two
 * rows of three, never five and an orphan — Love Spa's rule). Phones collapse
 * to one column regardless.
 */
function columnsSlot(options) {
    return {
        name: 'columns',
        kind: 'enum',
        label: 'Columns',
        max: 0,
        options,
        fallback: 'auto',
        hint: 'How many across on a wide screen. auto fits as many as the width allows; a number pins the count. Phones always stack.',
    };
}
/* ------------------------------------------------------------------ */
/* The pages                                                           */
/* ------------------------------------------------------------------ */
/**
 * The four page shapes a section may land on (`docs/site-section-system.md`
 * §9.2). `utility` — visits, cart, track, pay, the ticket — is deliberately
 * absent: those pages are chrome, styled by the preset, and carry no sections
 * at all, so a name for them here would be a name nothing could ever be
 * placed on.
 */
export const PAGE_KINDS = ['home', 'category', 'item', 'content'];
/* ------------------------------------------------------------------ */
/* The blocks                                                          */
/* ------------------------------------------------------------------ */
const VOCABULARY = {
    /**
     * `hero` — the first screen. `headline` has no fallback because the tenant's
     * *name* is the honest one and it comes from `/api/site`, not from config: a
     * site with no branding row still says who it is.
     */
    hero: {
        label: 'Hero',
        why: 'The first screen: the name, one line, and the way in to booking.',
        console_editable: true,
        pages: ['home', 'content'],
        slots: [
            {
                name: 'headline',
                kind: 'text',
                label: 'Headline',
                max: TEXT_MAX,
                hint: 'Left empty, the bundle uses the tenant’s own name from /api/site — so a site with no branding row still says who it is.',
            },
            { name: 'sub', kind: 'prose', label: 'Sub-heading', max: PROSE_MAX },
            {
                name: 'cta_label',
                kind: 'text',
                label: 'Button label',
                max: TEXT_MAX,
                fallback: 'Book a time',
            },
            {
                /**
                 * Where the words stand in the picture. `left` and `center` are the two
                 * every existing hero has; **`bottom`** is Six Senses
                 * (`docs/design/references/notes.md`, "Six Senses — `sixsenses-home.png`"):
                 * full-bleed video, the chrome reduced to a language switch and one
                 * pill, and *one serif sentence at the foot of the picture*. The quiet
                 * spa shape (§9.5 row 4) is written around that hero and had to settle
                 * for `cover` + `left` until this option existed (§9.7 phase 9, the
                 * first of the four workarounds).
                 *
                 * It is an alignment rather than a variant because the dataset and the
                 * view are unchanged — same photograph, same scrim, same one button;
                 * only the block the words sit in moves to the bottom edge.
                 */
                name: 'align',
                kind: 'enum',
                label: 'Alignment',
                max: 0,
                options: ['left', 'center', 'bottom'],
                fallback: 'left',
                hint: 'Where the words stand: left or centred in the picture, or bottom — one sentence at the foot of the photograph, the luxury hotel’s hero (Six Senses).',
            },
            {
                name: 'image',
                kind: 'image_url',
                label: 'Hero image (this block only)',
                max: IMAGE_SRC_MAX,
                hint: 'Overrides the image.hero token here. https: or an inline data:image URI — the bundle drops anything else.',
            },
            /*
             * The split layout and its two companions arrived together, from the
             * second real reference site (Pink Perry VI): a brand-first hero puts
             * the words BESIDE the photograph — headline left, picture right in a
             * framed card, mascot overlapping its corner — rather than on top of
             * it. `cover` is what every existing tenant has and stays the default;
             * additive, so the contract version holds (adding is free).
             */
            /*
             * `search` is the fifth view and the one the study called "a family"
             * (`docs/design/references/notes.md` §9 of the summary): treatment ·
             * place · time (Fresha), **party · date · time** (Resy, OpenTable),
             * **dates · guests** (Booking.com, Airbnb) — one bar, one button. §4.1's
             * Tables row says The Table's hero *is* that card, and its Stays row says
             * Stays' hero is the dates · guests bar; §9.1 records that the search
             * hero was the one hero variant still pending after phase 6.
             *
             * The bar is the *booking control's* restaurant and stay shape, not a
             * second one: the fields come from what the tenant sells (`prepared` and
             * the table's party count; `stay` and its dates; a departure's date), and
             * the hero mounts it as the page's primary control instead of a button.
             */
            variantSlot(['cover', 'split', 'minimal', 'editorial', 'search'], 'cover paints the image behind the words; split puts the words beside it in a framed card; minimal is type alone on the page ground, no photograph; editorial stacks the words over the photograph as a wide strip beneath them; search makes the trip bar itself the hero — party · date · time for a table, dates · guests for a stay — with the headline over it and one button.', 'layout'),
            {
                /**
                 * **Where the trip bar lives** — the one thing the captures disagree
                 * about. Booking.com and Fresha put the bar *on the hero*, big, under
                 * the headline; Resy and Airbnb put the same three segments **in the
                 * masthead**, small, beside the search box, and let the page below be
                 * editorial (`notes.md`: "The **trip bar lives in the masthead**",
                 * "The **search bar lives in the masthead**: Where · When · Who"). §4.1's
                 * Stays row calls the masthead placement a variant in so many words.
                 *
                 * So it is a slot on the block rather than two variants: the control is
                 * the same control and only its mount moves. Read by the `search` view
                 * only; `hero` keeps the bar on the hero, which is what a tenant who
                 * chooses the view without touching this gets.
                 */
                name: 'search_placement',
                kind: 'enum',
                label: 'Search bar placement',
                max: 0,
                options: ['hero', 'masthead'],
                fallback: 'hero',
                hint: 'Where the trip bar mounts on the search hero: hero puts it under the headline (Booking.com, Fresha); masthead puts it beside the nav, small, and leaves the hero editorial (Resy, Airbnb). Read by the search view only.',
            },
            {
                /**
                 * The accent word's flourish. Love Spa's "only yours" in gold *italic*
                 * was one rule of identity; the colour is `color.accent-2`'s and the
                 * slant is this. `plain` is every existing tenant's headline.
                 */
                name: 'accent_style',
                kind: 'enum',
                label: 'Accent word style',
                max: 0,
                options: ['plain', 'italic'],
                fallback: 'plain',
                hint: 'How the accent words are set: plain keeps the headline’s own style; italic slants them, the editorial flourish.',
            },
            {
                name: 'kicker',
                kind: 'text',
                label: 'Kicker',
                max: TEXT_MAX,
                hint: 'A short line above the headline — a place, a promise. Empty renders nothing.',
            },
            {
                /**
                 * How that line is set. `pill` is every existing hero; `tracked` is the
                 * eyebrow both Love Spa and AlpineCreek wrote the same six rules for on
                 * their dusk heroes — small caps, letter-spaced, no pill — which makes
                 * it a view, not identity.
                 */
                name: 'kicker_style',
                kind: 'enum',
                label: 'Kicker style',
                max: 0,
                options: ['pill', 'tracked'],
                fallback: 'pill',
                hint: 'pill sets the kicker in a small rounded panel; tracked sets it as a letter-spaced eyebrow with no panel, the cinematic hero’s.',
            },
            {
                name: 'headline_accent',
                kind: 'text',
                label: 'Accent words',
                max: TEXT_MAX,
                hint: 'A phrase inside the headline painted in the accent color, matched once, case-sensitively. Absent from the headline, it does nothing.',
            },
            {
                name: 'cta_target',
                kind: 'text',
                label: 'CTA target',
                max: TEXT_MAX,
                hint: 'Where the button goes, in the nav vocabulary (category:lodges, section:faq, a builtin). Empty keeps the booking anchor.',
            },
            {
                /**
                 * The cinematic cover: the reason the split layout was born is that
                 * the first cover hero "painted the headline ONTO the Jeep" — with a
                 * real photograph and a scrim, cover becomes what a lodge site
                 * actually wants: the image full-bleed, the words standing on a dusk
                 * gradient in light ink. `none` keeps every existing cover hero
                 * byte-identical.
                 */
                name: 'scrim',
                kind: 'enum',
                label: 'Cover scrim',
                max: 0,
                options: ['none', 'dusk'],
                fallback: 'none',
                hint: 'dusk lays a dark gradient over a cover photograph and flips the words light — the cinematic full-bleed hero. Ignored by the split layout.',
            },
        ],
    },
    /**
     * `services` — the catalog, from `/api/site`. Its content is **not** config:
     * prices come from the catalog and nowhere else (B7), so the slots here choose
     * how the list looks and what it is introduced by, never what is in it.
     */
    services: {
        label: 'Services',
        why: 'The bookable catalog from /api/site. Prices are the catalog’s (B7).',
        console_editable: true,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            {
                name: 'headline',
                kind: 'text',
                label: 'Headline',
                max: TEXT_MAX,
                fallback: 'What we offer',
            },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            /*
             * The last three are §4.2's services row finished and §9.1's "Missing:
             * tabs by category · groups with counts · price table", each from a
             * capture:
             *
             *  - **`tabs`** — Fresha's venue page runs "Services as category pills
             *    (Featured · Massage Therapy) over rows", and Vagaro's home puts the
             *    same pills under its search bar; `notes.md`'s adopted summary calls
             *    pills "the convergent shape for categories". One pill row, one list
             *    beneath it, the categories being the catalog's own.
             *  - **`groups`** — Booksy's are "collapsible groups ('Popular Services',
             *    'Other Services', 'Monthly Promotion') of rows … plus collapsed
             *    groups with a count chip ('7 services')". A long menu that a guest
             *    can fold.
             *  - **`price-table`** — name · duration · price rows, no card, no
             *    description: the studio's drop-in and packs and the barber's price
             *    list, which §9.5's studio row puts *early* as the intro offer. It is
             *    the fourth of phase 9's four workarounds (§9.7): The Studio template
             *    arranged `list` and said in its own comment that it would become
             *    this by changing one enum value.
             */
            variantSlot(['list', 'grid', 'cards', 'thumbnails', 'tabs', 'groups', 'price-table'], 'list runs the services down the page; grid lays them out as plain cards; cards is the menu as two-abreast glass cards — serif name, the price on a hairline in the second accent, the duration as a tracked label; thumbnails leads each with its catalog photograph; tabs puts the catalog’s categories as a row of pills over the rows (Fresha, Vagaro); groups folds each category into a collapsible panel with a count (Booksy); price-table is name · duration · price rows with no cards and no descriptions — the studio’s drop-in and packs, the barber’s list.', 'layout'),
            columnsSlot(['auto', '2', '3']),
            { name: 'show_price', kind: 'flag', label: 'Show price', max: 0, fallback: true },
            { name: 'show_duration', kind: 'flag', label: 'Show duration', max: 0, fallback: true },
            {
                /**
                 * The Book verb on every row (Fresha, Booksy): the menu section IS the
                 * chooser, and a row's button opens that treatment's own page with the
                 * flow already on it and the service chosen. Off by default — every
                 * existing page keeps its markup — and on for the templates.
                 */
                name: 'book_verb',
                kind: 'flag',
                label: 'Book button on each service',
                max: 0,
                fallback: false,
                hint: 'A Book button on every row, opening that service’s page with the booking flow on it.',
            },
        ],
    },
    /**
     * `hours` — opening times from the book, with the owner's words on top.
     *
     * `/api/site` projects `tenant_config.hours` (the row the scheduling engine
     * reads), so the block's *default* content is the same week that decides what
     * is bookable: change Sunday once and the page changes with it. A website
     * advertising hours the book no longer keeps is worse than one with no hours.
     *
     * `rows` is an **override**: an owner who types rows gets exactly those,
     * because "Sun–Mon closed, kitchen till 9" is a sentence and no projection of
     * a `DayHours[]` will ever be one.
     */
    hours: {
        label: 'Hours',
        why: 'Opening times from the tenant’s own week; typed rows override them.',
        console_editable: true,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Hours' },
            {
                name: 'rows',
                kind: 'rows',
                label: 'Rows (override)',
                max: 14,
                hint: 'Leave empty and the block prints the tenant’s real week from tenant_config.hours. Typed rows replace it entirely.',
            },
            { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX },
        ],
    },
    /** `story` — the words. A content zone (rung 4) with a plain-text fallback. */
    story: {
        label: 'Story',
        why: 'A content zone (rung 4) — the tenant’s own words, server-sanitized.',
        console_editable: true,
        pages: ['home', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Our story' },
            {
                name: 'body',
                kind: 'prose',
                label: 'Body (plain text)',
                max: PROSE_MAX,
                hint: 'Escaped by the bundle in the ordinary way. Used when the HTML zone is empty.',
            },
            {
                name: 'body_html',
                kind: 'richtext',
                label: 'Body (HTML content zone)',
                max: RICHTEXT_MAX,
                hint: 'Sanitized on publish. Wins over the plain-text body when present.',
            },
            { name: 'image', kind: 'image_url', label: 'Image', max: IMAGE_SRC_MAX },
            // The second half of the word-doc fix: a story whose image sits UNDER
            // four paragraphs is a document; one whose image sits BESIDE them is a
            // page. `stack` is every existing tenant's view and stays the default.
            variantSlot(['stack', 'split', 'split-reverse', 'zigzag', 'quote', 'editorial'], 'stack puts the image under the words; split puts it beside them (split-reverse: image on the left); zigzag runs the rows below as alternating picture-and-words; quote sets the body as one large quotation with the headline as its attribution and the image as a small portrait; editorial is the quiet stack — kicker, serif headline, prose, an underlined link.', 'layout'),
            {
                /**
                 * The zigzag's rows — the standard "about" on every service site:
                 * picture, words, picture, words, alternating sides. Read by the
                 * zigzag view only; the other views keep to `body` and `image`.
                 */
                name: 'items',
                kind: 'panels',
                label: 'Rows (zigzag)',
                max: 6,
                hint: 'Up to six { image, title, text, bullets, link, link_label } rows, alternating sides. Read by the zigzag view only.',
            },
            {
                name: 'link_label',
                kind: 'text',
                label: 'Link label',
                max: TEXT_MAX,
                hint: 'An underlined text link after the words — "Discover more". Empty renders nothing.',
            },
            {
                name: 'link_target',
                kind: 'text',
                label: 'Link target',
                max: TEXT_MAX,
                hint: 'Speaks the nav vocabulary: category:<slug>, section:<id>, item:<id>, or a builtin.',
            },
        ],
    },
    /**
     * `features` — the property's promises as an iconic row: fireplace, sauna,
     * skylight, creek. Born of the same AlpineCreek finding as the band system:
     * amenities expressed as prose bullets read as a document, and a features
     * row is the shape every hospitality site actually uses. Icons come from the
     * bundle's own closed set (`FEATURE_ICONS`) — chrome is ours; the words are
     * the tenant's.
     *
     * `console_editable: false` for now, deliberately: the block is arrangeable
     * and document-writable from day one (the Pink Perry lesson — arrangement
     * membership and console editability are different facts), and the console
     * control for an icon-picker row is its own small design, not worth blocking
     * the vocabulary on.
     */
    features: {
        label: 'Features',
        why: 'The property’s promises as an iconic row — amenities as a band, not as prose bullets.',
        console_editable: false,
        pages: ['home', 'category', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                /**
                 * **`icon` is optional, and the empty string is a legal value.**
                 *
                 * This is the second of phase 9's four workarounds (§9.7), and the fix
                 * belongs on this side of the contract rather than in the validator
                 * that happened to notice it. Three of the five views draw an icon;
                 * `quiet` draws none — *that is the whole of what makes it the luxury
                 * register* (Aman Wellness: "three text-only pillars with no icons and
                 * no cards") — and `stats` draws a figure where the icon would be. So a
                 * document using either view has nothing to name, and The Quiet Spa
                 * template carried three icon names it never drew, with a comment
                 * saying it only carried them because the strict write path refused an
                 * entry without one.
                 *
                 * A required field whose value is discarded by the view that needed the
                 * requirement waived is the shape of an accident, not of a rule. The
                 * contract's statement is therefore: `icon` may be absent or empty, the
                 * bundle draws nothing for it, and a *name* that is present must still
                 * be one the bundle draws (`FEATURE_ICONS`) — the closed set is about
                 * whose bytes the chrome is, and an empty icon brings no bytes.
                 */
                name: 'items',
                kind: 'features',
                label: 'Features',
                max: 6,
                hint: 'Up to six { icon, title, text, image, label } entries. icon is optional and may be empty — the quiet, stats and timeline views draw none — but a name that is there must come from the bundle’s closed set; image (https: or data:image) is read by the pillars and timeline views; label is the timeline’s timestamp and the numbered view’s counter when it is there.',
            },
            /*
             * `stats` is §9.6 decision 2's second half: three or four big numbers
             * with captions — "since 1829", "12 000 guests", "4 therapists" — is the
             * same dataset as a features row, an item's title and its text, drawn
             * differently. So it is a *variant*, not a block, and a tenant who wants
             * both writes two features blocks.
             */
            /*
             * `timeline` is the sixth view and the first one taken from a competitor
             * rather than from a reference a tenant would recognise:
             * `docs/design/references/timekey.md` §1 band 3 — "Your busiest Saturday,
             * handled", five timestamps from 06:40 to 21:05, each a real screen and
             * two sentences, and **the story is the CUSTOMER's day, never the
             * company's**. That is the same dataset a features row already holds —
             * a title, a line and a picture — with one field added (`label`, the
             * moment) and one drawn differently (the `image` as the screen beside the
             * words rather than as a card's cover). So it is a view, by the same rule
             * that made `stats` one.
             *
             * It is appended, so `icons` is still option zero and every published
             * document renders byte-for-byte.
             *
             * **`numbered` reads `label` too, and this is the explicit statement of
             * it**: where an entry carries one, the numbered view sets that word in
             * place of its counter, and where it does not, it counts 01 · 02 · 03 as
             * it always has. A view that silently ignored a field the block next to
             * it draws is the kind of quiet difference that turns into a bug report
             * about "the timestamps disappearing"; a view that *drew* it uninvited
             * would change existing pages, and no existing entry has a label.
             */
            variantSlot(['icons', 'pillars', 'quiet', 'numbered', 'stats', 'timeline'], 'icons is the iconic row every tenant has; pillars is three tall photo cards; quiet is columns of type alone — no icons, no cards, the luxury register; numbered counts them 01 · 02 · 03, or prints each item’s label in place of the counter where one is written; stats sets each item’s title as a large figure and its text as the caption — three or four big numbers ("since 1829"); timeline draws each item as a moment in one day — the label as the timestamp ("06:40"), the title, the text, and the image as the screen beside them.'),
            columnsSlot(['auto', '2', '3', '4']),
        ],
    },
    /**
     * `showcase` — the reveal accordion: a strip of photographic slices where
     * the open slice is a sliding WINDOW and the photograph under it never
     * moves. The technique is the owner's own (the JSWaves site's
     * carousel-accordion): slice and image animate left/width on the same
     * clock in opposite signs, so the motion reads as curtains over stationary
     * scenery — reveal, not transport. Taps select; the strip never captures a
     * vertical swipe (`touch-action: pan-y`, no drag handlers), because this
     * page scrolls and a component that eats the scroll is a trap.
     *
     * Each slice: a photograph, a short title, an optional line and up to four
     * bullets; the section may carry one CTA whose target speaks the nav
     * vocabulary (`category:lodges`, `section:faq`, a builtin). Repeatable —
     * the AlpineCreek home page runs several.
     */
    showcase: {
        label: 'Showcase',
        why: 'The reveal accordion — photographic slices that open like curtains over stationary scenery.',
        repeatable: true,
        console_editable: false,
        pages: ['home', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'items',
                kind: 'panels',
                label: 'Slices',
                max: 6,
                hint: 'Two to six { image, title, text, bullets, link, link_label } slices; the image is https: or an inline data:image URI, and a slice may carry its own destination in the nav vocabulary (item:<id>, category:<slug>).',
            },
            {
                name: 'link_label',
                kind: 'text',
                label: 'CTA label',
                max: TEXT_MAX,
                hint: 'The section’s one call to action. Empty renders no button.',
            },
            {
                name: 'link_target',
                kind: 'text',
                label: 'CTA target',
                max: TEXT_MAX,
                hint: 'Speaks the nav vocabulary: category:<slug>, section:<id>, or a builtin (visits, cart…).',
            },
            variantSlot(['strip', 'cards', 'mosaic'], 'strip is the reveal accordion — a strong signature that crops products badly; cards lays the slices out as photo cards with their words beneath, what a shop expects; mosaic sets the first large and the rest small beside it.'),
            LINKS_SLOT,
        ],
    },
    /**
     * `gallery` — **and it is repeatable, which is the feature.**
     *
     * The 24-image cap is per collection, not per page — three themed galleries of
     * twelve is the shape this exists for, and the ceiling that actually binds is
     * the whole branding row's byte budget.
     */
    gallery: {
        label: 'Gallery',
        why: 'Their photography, which §6.4.3 says does more than bespoke code. Repeatable — a page may carry several themed collections.',
        repeatable: true,
        console_editable: true,
        pages: ['home', 'category', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            {
                name: 'note',
                kind: 'prose',
                label: 'Intro',
                max: PROSE_MAX,
                hint: 'What makes this collection its own — “Adventures” and “Occasions” are different promises, and a grid of photographs cannot make either.',
            },
            {
                name: 'images',
                kind: 'images',
                label: 'Images',
                max: 24,
                hint: 'alt is required by the contract even when empty — a decorative photograph says so. The cap is per collection, not per page.',
            },
            variantSlot(['grid', 'feature', 'masonry', 'filmstrip', 'mosaic'], 'grid keeps the frames equal; feature leads with one large frame, magazine-style; masonry stacks mixed shapes in columns; filmstrip is one row that scrolls sideways; mosaic is one large frame with the rest tiled beside it, the venue-page shape.', 'layout'),
            columnsSlot(['auto', '2', '3', '4']),
        ],
    },
    /**
     * `faq` — *"Guests ask the same ten questions"* (`docs/tenant-site-offering.md`).
     *
     * Pairs rather than rich text, and that is the decision: what a tenant could
     * not do with the `story` zone is have them render as *disclosure widgets*,
     * and `<details>/<summary>` is the accessible, no-JavaScript, findable-by-
     * browser-search way to collapse them. The answer is prose, not a content
     * zone — a third path where tenant bytes become markup is not worth "do you
     * take card?".
     */
    faq: {
        label: 'FAQ',
        why: 'The same ten questions, as accessible disclosure widgets rather than a wall of answers.',
        console_editable: true,
        pages: ['category', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Questions' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'items',
                kind: 'qa',
                label: 'Questions and answers',
                max: 30,
                hint: 'Rendered as <details>/<summary>, so a keyboard opens them and the browser’s own find-in-page expands the matching one. Answers are plain text, not a content zone.',
            },
            /*
             * §4.2's faq row is **accordion · pills · two-column**, and the two that
             * were missing are the two shapes of "the same ten questions" the
             * references actually draw. `accordion` is the disclosure list made
             * *exclusive* — opening one closes the rest, which is what every platform
             * FAQ and Booksy's policies panel do, and it is the browser's own
             * `<details name>` grouping rather than script; `two-column` sets the
             * question and its answer side by side at desktop widths, the editorial
             * shape a content page of questions wants (the FAQ is a content-page
             * block since phase 7, and a wall of closed rows on a page whose whole
             * job is answering is a page that answers nothing above the fold).
             *
             * `list` stays first, so every document that names nothing — and the four
             * templates' `/p/faq` — renders exactly as it does today.
             */
            variantSlot(['list', 'pills', 'accordion', 'two-column'], 'list keeps the classic disclosures, any number of them open; pills renders the questions as a row of pills filling one answer card; accordion is the same disclosures made exclusive, so opening one closes the others; two-column sets each question beside its answer at desktop widths and stacks them on a phone.', 'layout'),
        ],
    },
    /**
     * `inquiry` — **request-to-book, for the offerings where a conversation beats
     * a calendar slot.**
     *
     * It is not a booking. Nothing here reserves anything, nothing is priced,
     * nothing touches the exclusion constraint, and the block deliberately does
     * not name a `service_id`. The route behind it takes no OTP — asking a
     * stranger to prove a phone number before they may ask a question is hostile,
     * and §6.4.4's point is that the OTP endpoint converts anonymous HTTP into
     * paid SMS.
     */
    inquiry: {
        label: 'Inquiry',
        why: 'Request-to-book for the offerings where a conversation beats a calendar slot. Never a booking, never a price.',
        console_editable: true,
        pages: ['home', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Ask us' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'occasions',
                kind: 'strings',
                label: 'Occasions offered',
                max: 12,
                entry_max: 80,
                hint: 'Leave empty and the guest gets a free-text box. A list is how a business says what it actually does — “Proposal”, “Anniversary”, “Island picnic”.',
            },
            {
                name: 'thanks',
                kind: 'prose',
                label: 'After sending',
                max: PROSE_MAX,
                fallback: 'Thank you — we have your note and will come back to you.',
            },
            {
                name: 'submit_label',
                kind: 'text',
                label: 'Button label',
                max: TEXT_MAX,
                fallback: 'Send',
            },
            variantSlot(['card', 'centered'], 'card is the form as every tenant has it, left-set; centered sets the words and the form on the page’s axis.'),
        ],
    },
    /**
     * `menu` — the menu and the basket (spec §3.6).
     *
     * `show_prices` exists because a tenant whose menu is a set-price tasting card
     * genuinely does not want a number beside every dish; `allow_ordering` exists
     * because a restaurant may want its menu on the page long before it wants
     * takeout. Neither can *add* anything: with `allow_ordering` on, ordering
     * still requires the tenant to be live and the guest to have proved an
     * identity, and the server refuses regardless of what a branding row says.
     */
    menu: {
        label: 'Menu',
        why: 'The menu and the basket (§3.6). Renders nothing unless the tenant has dishes.',
        console_editable: false,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Menu' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            { name: 'show_prices', kind: 'flag', label: 'Show prices', max: 0, fallback: true },
            { name: 'allow_ordering', kind: 'flag', label: 'Allow ordering', max: 0, fallback: true },
        ],
    },
    /**
     * `sailings` — the board (spec §4.8), a date and what is leaving on it.
     *
     * Like `services`, its content is not config. `days_shown` is a display width
     * rather than a business rule — the engine's horizon is the tenant's and this
     * cannot exceed it.
     */
    sailings: {
        label: 'Sailings',
        why: 'What is leaving and how many places are left (§4.8). Nothing unless the tenant runs trips.',
        console_editable: false,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            {
                name: 'headline',
                kind: 'text',
                label: 'Headline',
                max: TEXT_MAX,
                fallback: 'What’s sailing',
            },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            { name: 'days_shown', kind: 'number', label: 'Days shown', min: 1, max: 21, fallback: 7 },
        ],
    },
    /**
     * `stay` — the room search (spec §4.7): two dates, a party size, and what is
     * free.
     *
     * There is no "rooms" list slot and there will not be one. A rate is a
     * function of the dates asked about, so a block that listed room types with
     * prices would publish a number that is right for some nights and wrong for
     * others. The block asks first and quotes second.
     */
    stay: {
        label: 'Stay',
        why: 'Dates and party size → room types with a server-priced quote (§4.7).',
        console_editable: false,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Rooms' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'default_nights',
                kind: 'number',
                label: 'Nights offered by default',
                min: 1,
                max: 14,
                fallback: 2,
            },
        ],
    },
    /**
     * `booking` — where the flow mounts.
     *
     * A page whose config omits it still books: the shell offers the flow anyway.
     * A booking site that a tenant could accidentally configure into being unable
     * to take a booking would be a footgun dressed as flexibility.
     */
    booking: {
        label: 'Booking',
        why: 'Where the availability → OTP → confirm flow mounts.',
        console_editable: true,
        pages: ['home', 'item'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Book a time' },
            { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX },
            /**
             * The booking control from the study (`docs/site-section-system.md`
             * §4.1), behind a variant so the flow every tenant has stays the
             * default until the new one has run on the demo spa. `classic` is that
             * flow; `inline` is the study's control — the week strip with
             * availability before the tap, day parts, plain time chips, the person
             * as a choice, a summary that never leaves — with the summary as a card
             * in the page and a bar at the foot of a phone; `rail` is the same
             * control with the summary as a sticky column on the right.
             *
             * **`card` is the fourth, and it is a different shape rather than a
             * different summary.** The other three spread the control across the
             * page's width — a list of services on one side, the summary on the
             * other; this one is a single panel read top to bottom with the
             * **month** as its control: a calendar with availability marked on every
             * day, times two across under it, and one button that names the choice.
             * The reference is the checkout card in
             * `docs/design/references/timekey.md` §1 band 2, and the shape earns its
             * place where the schedule is **sparse** — a discovery call, a class that
             * runs twice a week, a charter — because a week strip of seven greyed
             * days asks a guest to page through a month seven days at a time. Where
             * a business is open every day and sells by the hour, `inline` and
             * `rail` still read better.
             */
            variantSlot(['classic', 'inline', 'rail', 'card'], 'classic is the flow every tenant has; inline is the studied control — week strip, day parts, time chips, the person as a choice, the summary always in reach; rail is the same with the summary as a sticky column beside the picker; card is one panel with the month as the control — availability marked on every day, times two across, and one button that names the choice, best where openings are sparse.'),
            {
                /**
                 * **What this band offers**, as catalog item ids in the author's order.
                 *
                 * The owner, on the product site's own booking band (2026-09-18):
                 * *"the discovery call is the one thing to book there, so we should
                 * have no option to pick from, no drop down"* — and then, on the
                 * second thought that made this a slot rather than a catalog edit:
                 * *"maybe the working session and the onboarding are things we want to
                 * be able to book too, but from the client's own portal, so this
                 * booking controller should be really configurable."*
                 *
                 * Those are two different facts and this is the line between them.
                 * **`bookable_online` is what the business sells to strangers**; this
                 * is **what one band asks about**. A tenant with twelve treatments can
                 * put a band on their winter page offering the three that are on
                 * offer, and the other nine keep their item pages, their partner
                 * surface and a band of their own somewhere else.
                 *
                 * Empty or absent is every service the catalog offers, which is what
                 * every published document means today and keeps meaning.
                 *
                 * Two rules the bundle owes, and they are the reason this is a list
                 * rather than a single id: **one service offered is no chooser at all**
                 * (no select, no pills, no rows — it is preselected and the control
                 * names it), and an id the catalog does not return is **dropped
                 * silently**, with a list that filters down to nothing falling back to
                 * every service rather than painting an empty control. An id is not
                 * checked for existence at write time, for the reason `item:<id>`
                 * links are not: ids are seed-minted, and a document may be published
                 * against a catalog that is reseeded under it.
                 *
                 * **Scope is this band's, not the flow's.** An item page's control is
                 * scoped to its own item and ignores this list — a visitor who
                 * followed a link to a treatment asked for that treatment explicitly,
                 * and a band on the home page has no standing to overrule them.
                 */
                name: 'services',
                kind: 'strings',
                label: 'Services offered here',
                max: 12,
                entry_max: ITEM_ID_MAX,
                entry_pattern: ITEM_ID_PATTERN,
                unique: true,
                hint: 'Catalog item ids, in the order they should be offered. Empty offers everything bookable online. One id means no chooser at all — the control is about that one thing. Ids the catalog no longer returns are ignored.',
            },
        ],
    },
    /**
     * The four section types the study found every template needs and the
     * registry did not have (`docs/site-section-system.md` §4.2). All four are
     * additive under contract v1: absent from every arrangement until a row
     * asks. The two that are lists of people or quotes ride the `panels` shape
     * the showcase already speaks, and like `features` and `showcase` they wait
     * for the console's list editor (`console_editable: false` says so); the
     * two that are words and a link are editable from day one.
     */
    /**
     * `testimonials` — the most requested section the registry lacked. Fresha's
     * review cards name the service and the person ("Deep Tissue Massage · with
     * Therapist 4"); Cal.com's is a strip of logos. Quotes are the tenant's own
     * words about themselves in somebody else's mouth, so they are plain text
     * like every slot here, never a content zone.
     */
    testimonials: {
        label: 'Testimonials',
        why: 'What guests said, as cards, one large quotation, or a strip of logos.',
        console_editable: false,
        pages: ['home', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'items',
                kind: 'panels',
                label: 'Quotes',
                max: 12,
                hint: 'Up to twelve { image, title, text, bullets } entries: image is a small portrait (optional), title the name, text the quotation, bullets the detail lines — the service, the person, the date.',
            },
            {
                name: 'images',
                kind: 'images',
                label: 'Logos',
                max: 12,
                hint: 'The logos view: up to twelve { src, alt } marks in one quiet row.',
            },
            variantSlot(['cards', 'single', 'logos'], 'cards lays the quotes out as a grid; single sets the first as one large quotation; logos draws the images as a strip of marks.'),
        ],
    },
    /**
     * `cta` — one sentence and one button, on the accent or the night ground:
     * "every template ends a chapter with one" (Booking.com's "Sign in, save
     * money", Fresha's app band). The button speaks the nav vocabulary and
     * nothing else, like every link in the bundle.
     */
    cta: {
        label: 'Call to action',
        why: 'One sentence and one button, the band that ends a chapter.',
        console_editable: true,
        pages: ['home', 'category', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            { name: 'note', kind: 'prose', label: 'One line under it', max: PROSE_MAX },
            {
                name: 'link_label',
                kind: 'text',
                label: 'Button label',
                max: TEXT_MAX,
                fallback: 'Book a time',
            },
            {
                name: 'link_target',
                kind: 'text',
                label: 'Button target',
                max: TEXT_MAX,
                hint: 'Speaks the nav vocabulary: section:<id>, category:<slug>, item:<id>, or a builtin. Empty keeps the booking anchor.',
            },
            /*
             * **The third of phase 9's four workarounds** (§9.7). The Quiet Spa
             * template wanted the quiet close and `cta` had no `variant` at all — a
             * select with one choice is a control that lies — so it was made with the
             * band vocabulary instead: a `paper` ground, no accent, one line and one
             * button, with a comment in `./templates.ts` explaining the substitution.
             * A tone is not a view: it says what ground the band sits on and cannot
             * say that the button is not a button.
             *
             *  - **`band`** is today's and stays first: one sentence and one filled
             *    button on the accent or night ground — Booking.com's "Sign in, save
             *    money", Fresha's app band.
             *  - **`minimal`** is Aman: "no buttons anywhere below the masthead's one
             *    'Reserve'", the close being a serif line and an **underlined text
             *    link** ("Discover more") on the page's own ground
             *    (`docs/design/references/notes.md`, Aman and Aman Wellness). §4.2's
             *    "luxury is the absence of chrome", as a view.
             *  - **`split`** puts the words at the left and the button at the right
             *    on one line — the wide one-row band Booking.com's offers card and
             *    every template's closing strip use when the sentence is short.
             */
            variantSlot(['band', 'minimal', 'split'], 'band is one sentence and one filled button on the band’s ground, the chapter-ending strip; minimal is one line and one underlined text link on the page ground with no fill — the quiet register’s close; split sets the words left and the button right on a single row.'),
        ],
    },
    /**
     * `team` — a spa sells a person, a tour sells a guide. Booksy puts the
     * staffers on the page as a row of avatars before the flow starts; "the
     * room" is a team of one. Owner decision 3 makes the person a *choice* in
     * the booking flow (phase 3); this block is where they are introduced.
     */
    team: {
        label: 'Team',
        why: 'The people, as cards, as a row of avatars, or one person beside their words.',
        console_editable: false,
        pages: ['home', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'items',
                kind: 'panels',
                label: 'People',
                max: 12,
                hint: 'Up to twelve { image, title, text, bullets } entries: the portrait, the name, a line or a paragraph, up to four facts.',
            },
            /*
             * `profile` is adapted from Preline UI's free "team profile card grid"
             * (MIT + Fair Use; credited in THIRD_PARTY_NOTICES.md) —
             * docs/sections-spike.md: a card with a small round portrait beside the
             * name and the role, the words underneath. Same data as `cards`; only
             * the arrangement is new.
             */
            variantSlot(['cards', 'avatars', 'one', 'profile'], 'cards is portraits with words beneath; avatars is one row of round portraits with names, the Booksy shape; one sets a single person’s portrait beside their words; profile is a card per person with a small round portrait beside the name and role, the words beneath.'),
        ],
    },
    /**
     * `location` — the bottom of every local business page: the address, the
     * week in one line, a still map, and the way there. The hours line comes
     * from the same `tenant_config.hours` the engine books against (typed
     * `note` words print under it); the map is a photograph the tenant brings —
     * no third-party tile request ever rides a guest's page — and "Get
     * directions" is the one external link the bundle builds itself, from the
     * address text, URL-encoded, to the maps search everyone's phone answers.
     */
    location: {
        label: 'Location',
        why: 'The address, the week in a line, a still map and the way there.',
        console_editable: true,
        pages: ['home', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Find us' },
            {
                name: 'address',
                kind: 'prose',
                label: 'Address',
                max: PROSE_MAX,
                hint: 'As it should be read aloud, line breaks kept. It is also what "Get directions" searches for.',
            },
            { name: 'phone', kind: 'text', label: 'Telephone', max: TEXT_MAX },
            {
                name: 'map',
                kind: 'image_url',
                label: 'Map image',
                max: IMAGE_SRC_MAX,
                hint: 'A still map or an aerial photograph, https: or an inline data:image URI. The bundle never loads a map from a third party.',
            },
            {
                name: 'show_hours',
                kind: 'flag',
                label: 'Show the week in a line',
                max: 0,
                fallback: true,
            },
            { name: 'directions', kind: 'flag', label: 'Offer directions', max: 0, fallback: true },
            { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX },
            /*
             * The coordinates (GS9, C4): what a `geo` node in the structured data
             * and a pin-exact map link are made of. Text rather than number, because
             * a coordinate is a decimal somebody pastes from their phone and the
             * bundle is the one that checks it is on the globe — a slot that refused
             * `18.3419` for not being an integer would be a slot nobody could fill.
             * Both empty is every existing row, and then the address alone is what
             * the map search and the node carry.
             */
            {
                name: 'latitude',
                kind: 'text',
                label: 'Latitude',
                max: TEXT_MAX,
                hint: 'Decimal degrees, e.g. 18.3419. With longitude, the map link and the search listing point at the door rather than at the address text.',
            },
            {
                name: 'longitude',
                kind: 'text',
                label: 'Longitude',
                max: TEXT_MAX,
                hint: 'Decimal degrees, e.g. -64.9307.',
            },
            /*
             * The masthead line (GS9, D5) — the token the verticals survey asked for:
             * the address and today's hours in the bar, on every page, for the
             * restaurant and the lodge whose visitor is deciding whether to drive.
             * It is a flag on this block rather than a masthead token because the
             * words are *this block's* — one address typed once — and the bar only
             * repeats them.
             */
            {
                name: 'in_masthead',
                kind: 'flag',
                label: 'Address and today’s hours in the masthead',
                max: 0,
                fallback: false,
            },
            /*
             * The second view (GS9, C5). `classic` is every existing row: the words
             * and, beside them, the still map when there is one. `card` is the map
             * card a local business lives on in a search result, drawn on the page
             * itself: the still map as the cover, the address, today's hours in one
             * line, the week, the telephone and one outbound maps link — still no
             * third-party tile, still a link the guest opens.
             */
            variantSlot(['classic', 'card'], 'classic sets the address beside the still map, the band every existing page has; card draws the map card a local business gets in a search result — the map as cover, then the address, today’s hours, the week and one outbound maps link.'),
        ],
    },
    footer: {
        label: 'Footer',
        why: 'Address, telephone, and the tenant’s own fine print.',
        console_editable: true,
        pages: ['home', 'category', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX },
            { name: 'rows', kind: 'rows', label: 'Rows', max: 8 },
            {
                name: 'html',
                kind: 'richtext',
                label: 'Fine print (HTML content zone)',
                max: RICHTEXT_MAX,
                hint: 'Sanitized on publish.',
            },
            LINKS_SLOT,
        ],
    },
    /**
     * The ten the second planning pass added (`docs/site-section-system.md` §9.1).
     * Six are bands the survey found on real pages and the registry did not have;
     * four are the fixed parts of the category and item pages promoted to
     * vocabulary entries. All ten are additive under contract v1 — absent from
     * every arrangement until a row asks — and all ten declare their `pages`,
     * which is the point of the pass: the inventory did not grow past what a home
     * page is made of, it got *placed*.
     */
    /**
     * `notice` — the ribbon near the top of the page.
     *
     * The survey (`docs/design/references/homepage-sequences.md`) found one on 6
     * of 31 real sites, and **five of the six carry an offer** rather than a
     * notice: "$25 off", "VIP club", "summer special". So the block ships with
     * its promotional face as a first-class view instead of as a closure line
     * somebody typed an offer into — `closure` is the plain line a holiday or a
     * weather day wants, `promo` draws the link as a button (§9.6 decision 3).
     *
     * It is the one block offered on all four pages: a closure is true of the
     * business, not of a page, and a shop that has shut for a hurricane should be
     * able to say so above a treatment as well as above the home.
     */
    notice: {
        label: 'Notice',
        why: 'One line near the top — a closure, or the offer five of six real ribbons actually carry.',
        console_editable: true,
        pages: ['home', 'category', 'item', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'text', kind: 'text', label: 'The line', max: TEXT_MAX },
            { name: 'link_label', kind: 'text', label: 'Link label', max: TEXT_MAX },
            {
                name: 'link_target',
                kind: 'text',
                label: 'Link target',
                max: TEXT_MAX,
                hint: 'Speaks the nav vocabulary, like the cta’s: section:<id>, category:<slug>, item:<id>, or a builtin. Empty renders no link.',
            },
            variantSlot(['closure', 'promo'], 'closure is a plain line — holiday hours, a weather day; promo is an offer with the link drawn as a button, which is what five of the six ribbons in the survey are.'),
        ],
    },
    /**
     * `policies` — the cancellation window, the deposit, what to bring, as check
     * rows. Its own block rather than a `features` variant (§9.6 decision 2)
     * because the data source is different: this is what the business *requires*,
     * not what it promises. Real sites that carry it put it last, and it is an
     * item-page and content-page block — a home page that opens with its refund
     * terms is selling nothing.
     *
     * **The settings feed landed** (GS2; the owner's ruling 2 of 2026-09-21).
     * `/api/site` carries `site.policies` — the rules the engine keeps, derived
     * from `tenant_config` (`derivedPolicies` in `@yourstore/domain`), first; then
     * these rows, minus any whose `kind` claims a rule the engine derives. When
     * the server publishes that list, **every `policies` block on the site draws
     * the canonical list** — the first block by the profile's own first-occurrence
     * rule — rather than its own `rows`, so two blocks cannot state two policies.
     * A row typed here about cancellation is still the tenant's own words, and
     * the only words: the engine prices no cancellation and derives none.
     *
     * No variant slot: there is one view, and a block gains `variant` with its
     * second view and never before — a select with one choice is a control that
     * lies (see `variantSlot`).
     */
    policies: {
        label: 'Policies',
        why: 'Cancellation, deposit, what to bring — what the business requires, as check rows.',
        console_editable: true,
        pages: ['item', 'content'],
        slots: [
            ...BAND_SLOTS,
            {
                name: 'headline',
                kind: 'text',
                label: 'Headline',
                max: TEXT_MAX,
                fallback: 'Good to know',
            },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'rows',
                kind: 'rows',
                label: 'Policies',
                max: 8,
                hint: 'label/value pairs — “Cancellation” / “24 hours’ notice”, “Deposit” / “50 % on booking” — drawn as check rows.',
            },
        ],
    },
    /**
     * `gift` — buy a gift card. In every spa reference's rail and a nav item on
     * most real spas, a band on one (§9.1), which is why it is offered on the
     * home and nowhere else: it is a cross-sell on the page a stranger lands on,
     * not something a treatment page should interrupt itself with.
     *
     * **The residual.** The guest site has **no certificate purchase flow** — the
     * gift-credit ledger exists behind the counter, and nothing on the guest wire
     * sells one. So `link_target` left empty points at the inquiry block's
     * anchor, which is the honest destination today: the guest asks, and a person
     * answers. When the purchase flow lands, the empty default becomes it and no
     * tenant row has to change.
     */
    gift: {
        label: 'Gift cards',
        why: 'The cross-sell every spa reference puts in its rail — give a gift.',
        console_editable: true,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Give a gift' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            { name: 'image', kind: 'image_url', label: 'Image', max: IMAGE_SRC_MAX },
            {
                name: 'link_label',
                kind: 'text',
                label: 'Button label',
                max: TEXT_MAX,
                fallback: 'Buy a gift card',
            },
            {
                name: 'link_target',
                kind: 'text',
                label: 'Button target',
                max: TEXT_MAX,
                hint: 'Speaks the nav vocabulary: section:<id>, category:<slug>, item:<id>, or a builtin. Empty points at the inquiry block’s anchor — the guest site has no certificate purchase flow yet, so asking is the honest destination.',
            },
        ],
    },
    /**
     * `shop` — the stocked items, with add-to-cart, from the catalog.
     *
     * Catalog-driven like `menu`, and `console_editable: false` for the same
     * reason: the content comes from `/api/site` rather than from a branding row,
     * so there is nothing for an operator to type. It **renders nothing unless
     * the tenant has stocked items**, which is what lets it sit in an arrangement
     * a spa and a print shop both use.
     *
     * The Shelf template's `showcase cards` is *authored* content — a picture, a
     * title and a line somebody wrote — and this is the catalog: Love Spa's shelf
     * and Alpine Creek's prints need real items with real prices, and B7 says
     * those come from the catalog and nowhere else. The survey found products on
     * 6 of 31 real homes, always late on the page.
     */
    shop: {
        label: 'Shop',
        why: 'Stocked items from the catalog, with add-to-cart. Nothing unless the tenant stocks anything.',
        console_editable: false,
        pages: ['home'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'The shelf' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            { name: 'show_prices', kind: 'flag', label: 'Show prices', max: 0, fallback: true },
            LINKS_SLOT,
        ],
    },
    /**
     * `timetable` — a week grid of classes with places left: the services whose
     * `max_party` is greater than one, which is what a class *is* in this
     * catalog. Catalog-driven, so `console_editable: false` and it renders
     * nothing unless the tenant has such services.
     *
     * **No surveyed studio puts it on the home** — every one links out to
     * Mindbody, Momence or Punchpass — so it is the classes *category page's*
     * block and merely offered on the home (§9.1, §9.5's studio shape). Their
     * absence is partly tooling: theirs is a page they cannot embed and ours is
     * not, which is why it stays offered rather than removed.
     */
    timetable: {
        label: 'Timetable',
        why: 'The week’s classes with places left — the classes category page’s band, optional on the home.',
        console_editable: false,
        pages: ['home', 'category'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'This week' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            { name: 'days_shown', kind: 'number', label: 'Days shown', min: 1, max: 14, fallback: 7 },
        ],
    },
    /**
     * `newsletter` — the band that captures the interest, and nothing more
     * (`docs/newsletter-design.md`; §9.6 decision 4). One field, one button, a
     * consent line: on every surveyed site that is the whole band, and nothing
     * asks for a name. 7 of 31 real sites and 5 of 17 templates carry one, always
     * in the bottom third and usually the closing band before the footer.
     *
     * Typing an address here is **never an identity claim** — it stores a fact
     * about an address in a table of its own, creates no client row, and signs
     * nobody in; account claim stays the OTP flow. That is the design document's
     * decision, and the slots below are its table, letter for letter.
     */
    newsletter: {
        label: 'Newsletter',
        why: 'One field, one button, a consent line — the interest captured, delivery a separate conversation.',
        console_editable: true,
        pages: ['home', 'content'],
        slots: [
            ...BAND_SLOTS,
            {
                name: 'headline',
                kind: 'text',
                label: 'Headline',
                max: TEXT_MAX,
                fallback: 'Stay in touch',
            },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'submit_label',
                kind: 'text',
                label: 'Button label',
                max: TEXT_MAX,
                fallback: 'Sign up',
            },
            {
                name: 'consent',
                kind: 'prose',
                label: 'Consent line',
                max: PROSE_MAX,
                fallback: 'By signing up you agree to receive news from us. Unsubscribe any time.',
                hint: 'The sentence under the field, naming the tenant as the sender. The tenant may reword it.',
            },
            {
                name: 'thanks',
                kind: 'prose',
                label: 'After signing up',
                max: PROSE_MAX,
                fallback: 'Thank you — you are on the list.',
            },
            variantSlot(['inline', 'card', 'footer'], 'inline is the field beside the button on a band; card sets the headline, the note and the form on a card; footer folds it into the footer’s columns.'),
        ],
    },
    /**
     * `pricing` — **the plans, as tiers.**
     *
     * §9.1's "deliberately not sections" list has carried *pricing tiers and
     * memberships* since the inventory pass, and the reason it gave was right for
     * every tenant on the platform: a spa has no tiers, and the numbers on a spa's
     * page are the catalog's, decided by the server (B7). A block that let an
     * operator type a price beside a treatment would be the first place on the
     * guest site where a published number and a charged number could disagree.
     *
     * **A product site is the case that changes it, and it changes it without
     * touching that reasoning.** The site that sells *this platform* to operators
     * (JSWaves, `docs/clients.md` client 1) has plans, and a plan is not a catalog
     * item: nothing here is bookable, nothing here is orderable, no tier has an
     * `item_id`, and the block never reads `/api/site`. The words are authored the
     * way a headline is authored — `price` is free text precisely so that it can
     * say "Custom" — and **this block never prices a booking**: a guest flow that
     * wanted a number would still get it from the catalog, and there is no slot
     * here that a checkout could ever read.
     *
     * The evidence is a competitor's home page read band by band
     * (`docs/design/references/timekey.md` §1 band 11: three tiers, one marked
     * most popular, the fine print under them) and the two product sites in
     * `docs/design/references/notes.md` — Cal.com and Calendly, whose homes are
     * the split hero with the product as the picture that this block's template
     * also borrows. §5.1 of the TimeKey note names pricing tiers as one of exactly
     * two blocks our vocabulary lacked for a product site; the other is the
     * `features.timeline` view above.
     *
     * **No variant slot.** There is one view — the columns — and a block gains
     * `variant` with its second and never before (see `variantSlot`).
     *
     * `console_editable: false`, like `features` and `showcase`: a tier is a list
     * entry with a list inside it, and the console's list editor is its own
     * design. The block is arrangeable and document-writable from day one, which
     * is the Pink Perry lesson — arrangement membership and console editability
     * are different facts.
     */
    pricing: {
        label: 'Pricing',
        why: 'The plans as tiers — authored words on a product site, never the catalog and never a booking’s price.',
        console_editable: false,
        pages: ['home', 'content'],
        slots: [
            ...BAND_SLOTS,
            { name: 'headline', kind: 'text', label: 'Headline', max: TEXT_MAX, fallback: 'Pricing' },
            { name: 'note', kind: 'prose', label: 'Intro', max: PROSE_MAX },
            {
                name: 'items',
                kind: 'tiers',
                label: 'Tiers',
                max: TIERS_MAX,
                hint: 'Up to four { name, price, period, note, bullets, link_label, link_target, featured } plans. price is free text — “$79”, “Custom”, “From $3k” — never a catalog number; link_target speaks the nav vocabulary; at most one tier may be featured.',
            },
            {
                /**
                 * The line under the columns. Every surveyed pricing band has one and
                 * it is always the same two things: what the trial is ("14-day full
                 * trial · No credit card · Cancel anytime") and what a percentage
                 * applies to. It is a slot of its own rather than a fourth sentence in
                 * `note` because it sits *under* the tiers rather than over them, and
                 * because it is the one piece of copy on the band that is true of every
                 * column — putting it in a tier's `note` would be publishing it three
                 * times and letting two of them rot.
                 */
                name: 'fine_print',
                kind: 'prose',
                label: 'Fine print',
                max: PROSE_MAX,
                hint: 'The quiet line under the tiers — trial terms, what a commission applies to, when the price changes. Empty renders nothing.',
            },
        ],
    },
    /**
     * The four page-specific parts (§9.6 decision 6).
     *
     * These are what the category and item pages are already made of — today
     * fixed markup in `apps/site/src/pages/Pages.tsx` — promoted to vocabulary
     * entries so they can carry variants like every other band. Nothing about
     * either page changes by their existing: each one's first view is today's
     * rendering, and a page whose document names none of them renders exactly as
     * it does now.
     *
     * `console_editable: false` and no `variant` yet, both for the same reason:
     * there is one view and nothing to type. The variants pass (§9.7 phase 9) is
     * what gives them a second, and the `variant` slot arrives with it. The
     * console never offers them on the home — their `pages` say so.
     */
    category_header: {
        label: 'Category header',
        why: 'The category page’s name, blurb and cover — today a fixed part of that page.',
        console_editable: false,
        pages: ['category'],
        slots: [...BAND_SLOTS, { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX }],
    },
    collection: {
        label: 'Collection',
        why: 'The category’s item grid — today a fixed part of that page.',
        console_editable: false,
        pages: ['category'],
        slots: [...BAND_SLOTS, { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX }],
    },
    item_header: {
        label: 'Item header',
        why: 'The item page’s media, name and from-price — today a fixed part of that page.',
        console_editable: false,
        pages: ['item'],
        slots: [...BAND_SLOTS, { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX }],
    },
    related: {
        label: 'Related items',
        why: 'The item page’s “also” row — today a fixed part of that page.',
        console_editable: false,
        pages: ['item'],
        slots: [...BAND_SLOTS, { name: 'note', kind: 'prose', label: 'Note', max: PROSE_MAX }],
    },
};
/**
 * The registry, keyed by block name.
 *
 * Declared `as const` for the key union and then *widened* to the interface,
 * because a caller iterating the table has to be able to ask any slot for its
 * `min` or its `options` — under the const type those properties exist only on
 * the two or three literal objects that happen to carry them, which makes a
 * generic walk over the vocabulary impossible to write without a cast.
 */
export const SECTION_VOCABULARY = VOCABULARY;
export const SECTION_TYPES = Object.keys(VOCABULARY);
export function isSectionType(value) {
    return Object.prototype.hasOwnProperty.call(SECTION_VOCABULARY, value);
}
/**
 * The blocks the operator console renders controls for.
 *
 * Still the `console_editable` filter and nothing else. The **page** filter is
 * a second, independent question — a block can be editable everywhere and
 * belong on one page — and it is the console's to apply, with
 * `sectionsForPage`, at the moment it knows which page is being arranged.
 */
export const CONSOLE_SECTION_TYPES = SECTION_TYPES.filter((type) => SECTION_VOCABULARY[type].console_editable);
/**
 * The blocks that may appear on one page, in registry order
 * (`docs/site-section-system.md` §9.3).
 *
 * Registry order, not matrix order, for the reason the registry is ordered at
 * all: it is the one sequence both surfaces already walk, so a console list and
 * a bundle's warning name blocks in the same order without either holding a
 * second list. What belongs on a page *by default* is a template's statement
 * (`./templates.js`), not this function's — this answers only "may it be here".
 */
export function sectionsForPage(page) {
    return SECTION_TYPES.filter((type) => SECTION_VOCABULARY[type].pages.includes(page));
}
/**
 * **Blocks the home page may still carry, although §9.3 places them elsewhere.
 * Empty — and kept.**
 *
 * It held `faq` through phase 6, and the reason was a date rather than a
 * design: §9.3 puts the FAQ on the category, item and content pages and §9.4
 * says plainly that it is not a home band, but every document that existed —
 * the three dressed tenants (`spikes/demospa`, `pinkperry`, `alpinecreek`) and
 * four of the six templates — carried one there, and **there was nowhere else
 * for it to go**. A write path that had refused it the day the matrix landed
 * would have refused every reseed, which is placing the inventory by breaking
 * the fleet. So a grandfathered block was accepted on the home by the write
 * path and stripped from nothing, while never being *offered* — the console
 * builds its chooser from `sectionsForPage`, which this list does not widen —
 * so no new document could acquire one while the old ones kept rendering.
 *
 * **Phase 7 emptied it.** The content page is a real address now
 * (`./content.ts`, `PAGE_PATHS.page`), the four templates moved their FAQ band
 * into `pages.faq` with a `page:faq` entry in the bar, and every side that
 * reads this constant flipped in one commit — which is the whole of what the
 * mechanism was for.
 *
 * The constant stays at `[]` rather than being deleted. It is the honest way
 * to land the *next* placement change: phases 8–9 re-cut the home shapes and
 * add variants, and the next block the matrix moves off a page will need
 * exactly this — one line, read by every validator, that says "accepted where
 * it already is, never offered anywhere new, emptied when the documents have
 * moved". Re-deriving that mechanism under deadline is how a tolerance ends up
 * growing in three validators instead.
 */
export const HOME_GRANDFATHERED = [];
/**
 * What a tenant who has configured nothing gets: say who you are, show what you
 * sell — **including the vertical you sell it in** — say when you are open, take
 * the booking, print the address. In the order a stranger reads them.
 *
 * `hours` sits after the catalog blocks and before `booking` for the reason
 * ordering exists at all: it is the question a person asks *after* "what do you
 * do" and *before* "can I come Thursday", and answering it late is answering it
 * after they have already guessed.
 *
 * **The three vertical blocks are here for the same reason, and it is the whole
 * of "a new tenant is three rows and zero deployments" (§6.4.2).** Each renders
 * *nothing at all* unless `/api/site` says the tenant sells it — a projection of
 * the catalog, never a configuration flag — so a spa's default page is
 * byte-identical to what it was before this list grew. The alternative was a
 * default arrangement per vertical, which is a fork of the shared bundle wearing
 * a constant's clothes (§6.4.3 rule 1).
 */
export const DEFAULT_ARRANGEMENT = [
    'hero',
    'services',
    'menu',
    'sailings',
    'stay',
    'hours',
    'booking',
    'footer',
];
/**
 * **`booking` always mounts.**
 *
 * The bundle's shell offers the booking flow whether or not the block is in the
 * arrangement. The console therefore pins it rather than pretending it can be
 * removed — excluding it would only move where the flow appears, never whether
 * it appears, and a checkbox that lies is worse than no checkbox.
 */
export const PINNED_SECTION = 'booking';
/** The `id` a section may carry — a React key and an anchor, nothing more. */
export const SECTION_ID_PATTERN = /^[a-z0-9-]{1,40}$/i;
/** The slot table for one block, or `undefined` for a name that is not one. */
export function sectionSlots(type) {
    return isSectionType(type) ? SECTION_VOCABULARY[type].slots : undefined;
}
//# sourceMappingURL=sections.js.map